import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Filter, Folder, LayoutList, ListChecks, Plus, Search } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import TableCard from '@/components/layout/TableCard'
import Select from '@/components/ui/Select'
import ActionMenu from '@/components/ui/ActionMenu'
import OwnerAvatar from '@/components/OwnerAvatar'
import ListFormModal from '@/features/lists/ListFormModal'
import FoldersPanel from '@/features/lists/FoldersPanel'
import FolderIcon from '@/features/lists/FolderIcon'
import { useListActions } from '@/features/lists/useListActions'
import TypeBadge from '@/features/lists/TypeBadge'
import { useListFolders, useListStats, useListsPage } from '@/features/lists/useLists'
import { useMembers } from '@/features/team/useTeam'
import { pageItems } from '@/lib/pageItems'
import { formatShortDate } from '@/lib/dealFormat'
import { cn } from '@/lib/cn'
import type { ContactList, ListType } from '@/api/lists'

const SORTS = {
  updated: { sort: 'updatedAt', dir: 'desc', label: 'Recently updated' },
  name: { sort: 'name', dir: 'asc', label: 'Name A–Z' },
  newest: { sort: 'createdAt', dir: 'desc', label: 'Newest first' },
} as const
type SortKey = keyof typeof SORTS
const PAGE_SIZES = [10, 25, 50]

function StatTile({
  label,
  value,
  icon,
  active,
  onClick,
}: {
  label: string
  value: number | undefined
  icon: React.ReactNode
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-colors',
        active ? 'border-brand-primary ring-1 ring-brand-primary' : 'border-gray-200 hover:border-gray-300',
      )}
    >
      <span className="w-10 h-10 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
        {icon}
      </span>
      <span>
        <span className="block text-2xl font-semibold text-brand-darkest tabular-nums leading-tight">{value ?? '—'}</span>
        <span className="block text-xs text-gray-500">{label}</span>
      </span>
    </button>
  )
}

/**
 * Lists: the contact segments behind campaigns and sequences. Full-width table (size, type, folder,
 * owner, where it's used), headline counts that double as filters, a folders panel, and every action on
 * a list one ⋯ away. Opening a list goes to its own page.
 */
export default function Lists() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const folderId = params.get('folder') ?? ''
  const type = (params.get('type') ?? '') as '' | ListType
  const ownerId = params.get('owner') ?? ''
  const sortKey = (params.get('sort') as SortKey) in SORTS ? (params.get('sort') as SortKey) : 'updated'
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [creating, setCreating] = useState(false)
  const [foldersOpen, setFoldersOpen] = useState(false)

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
    setPage(0)
  }

  const { data: stats } = useListStats()
  const { data: folders } = useListFolders()
  const { data: members } = useMembers()
  const memberById = new Map((members ?? []).map((m) => [m.id, m]))
  const { sort, dir } = SORTS[sortKey]
  const { data, isLoading } = useListsPage({
    page,
    size,
    sort,
    dir,
    q: q || undefined,
    folderId: folderId || undefined,
    type: type || undefined,
    ownerId: ownerId || undefined,
  })
  const rows = data?.items ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / size))
  const filtered = !!(q || folderId || type || ownerId)
  const activeFolder = (folders ?? []).find((f) => f.id === folderId)

  const open = (l: ContactList) => navigate(`/lists/${l.id}`)
  const actions = useListActions({ onView: open, onDuplicated: open })

  return (
    <PageContainer width="full">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-brand-darkest">Lists</h1>
          <p className="text-sm text-gray-500">Groups of contacts for campaigns and sequences.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFoldersOpen((o) => !o)}
            aria-pressed={foldersOpen}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium',
              foldersOpen ? 'border-brand-primary text-brand-primary bg-brand-primary/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50',
            )}
          >
            <Folder size={15} /> Folders
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
          >
            <Plus size={15} /> New list
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {/* One tile lit at a time: the open Folders panel takes the highlight from the list filters. */}
        <StatTile label="Lists" value={stats?.total} icon={<LayoutList size={18} />} active={!foldersOpen && !type && !folderId} onClick={() => { setParams({}, { replace: true }); setPage(0); setFoldersOpen(false) }} />
        <StatTile label="Dynamic" value={stats?.dynamic} icon={<Filter size={18} />} active={!foldersOpen && type === 'dynamic'} onClick={() => { setParam('type', type === 'dynamic' ? '' : 'dynamic'); setFoldersOpen(false) }} />
        <StatTile label="Static" value={stats?.manual} icon={<ListChecks size={18} />} active={!foldersOpen && type === 'manual'} onClick={() => { setParam('type', type === 'manual' ? '' : 'manual'); setFoldersOpen(false) }} />
        <StatTile label="Folders" value={stats?.folders} icon={<Folder size={18} />} active={foldersOpen} onClick={() => setFoldersOpen((o) => !o)} />
      </div>

      <div className={cn('grid gap-4', foldersOpen && '2xl:grid-cols-[1fr_18rem]')}>
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-48 max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0) }}
                placeholder="Search lists…"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
              />
            </div>
            <Select size="md" value={type} onChange={(e) => setParam('type', e.target.value)} aria-label="Type">
              <option value="">All types</option>
              <option value="dynamic">Dynamic</option>
              <option value="manual">Static</option>
            </Select>
            <Select size="md" value={folderId} onChange={(e) => setParam('folder', e.target.value)} aria-label="Folder">
              <option value="">All folders</option>
              {(folders ?? []).map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </Select>
            <Select size="md" value={ownerId} onChange={(e) => setParam('owner', e.target.value)} aria-label="Owner">
              <option value="">All owners</option>
              {(members ?? []).map((m) => (
                <option key={m.id} value={m.id}>{m.fullName}</option>
              ))}
            </Select>
            <Select size="md" value={sortKey} onChange={(e) => setParam('sort', e.target.value === 'updated' ? '' : e.target.value)} aria-label="Sort">
              {Object.entries(SORTS).map(([k, s]) => (
                <option key={k} value={k}>Sort: {s.label}</option>
              ))}
            </Select>
            {filtered && (
              <button type="button" onClick={() => { setQ(''); setParams({}, { replace: true }); setPage(0) }} className="text-sm text-brand-primary hover:underline">
                Clear filters
              </button>
            )}
          </div>

          {activeFolder && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FolderIcon color={activeFolder.color} open size={17} />
              <span className="font-semibold text-brand-darkest">{activeFolder.name}</span>
              <span className="text-gray-400">· {activeFolder.listCount} list{activeFolder.listCount === 1 ? '' : 's'}</span>
            </div>
          )}

          <TableCard
            footer={
              total > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm text-gray-500">
                  <span>
                    Showing {page * size + 1}–{Math.min(total, (page + 1) * size)} of {total} list{total === 1 ? '' : 's'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Previous page">
                      <ChevronLeft size={16} />
                    </button>
                    {pageItems(page + 1, pages).map((p, i) =>
                      p === '…' ? (
                        <span key={`gap${i}`} className="px-1.5">…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPage(p - 1)}
                          aria-current={p === page + 1 ? 'page' : undefined}
                          className={cn('min-w-8 h-8 rounded-lg text-sm', p === page + 1 ? 'bg-brand-primary text-white' : 'hover:bg-gray-100')}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Next page">
                      <ChevronRight size={16} />
                    </button>
                    <Select size="sm" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0) }} aria-label="Lists per page" className="ml-2">
                      {PAGE_SIZES.map((s) => (
                        <option key={s} value={s}>{s} per page</option>
                      ))}
                    </Select>
                  </div>
                </div>
              ) : undefined
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50/60">
                  <th className="px-4 py-2.5">List name</th>
                  <th className="px-3 py-2.5 text-right">Contacts</th>
                  <th className="px-3 py-2.5">Type</th>
                  <th className="px-3 py-2.5">Folder</th>
                  <th className="px-3 py-2.5">Owner</th>
                  <th className="px-3 py-2.5">Used in</th>
                  <th className="px-3 py-2.5">Updated</th>
                  <th className="px-3 py-2.5 w-28"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const owner = l.ownerUserId ? memberById.get(l.ownerUserId) : undefined
                  return (
                    <tr key={l.id} onClick={() => open(l)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80 cursor-pointer">
                      <td className="px-4 py-3 min-w-[12rem] max-w-[22rem]">
                        <Link to={`/lists/${l.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-brand-darkest hover:text-brand-primary">
                          {l.name}
                        </Link>
                        {l.description && <div className="text-xs text-gray-500 truncate">{l.description}</div>}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-brand-darkest">{l.memberCount.toLocaleString()}</td>
                      <td className="px-3 py-3"><TypeBadge type={l.listType} /></td>
                      <td className="px-3 py-3">
                        {l.folderName ? (
                          <span className="inline-flex items-center gap-1.5 text-gray-700">
                            <FolderIcon color={l.folderColor} /> {l.folderName}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {owner ? (
                          <span className="inline-flex items-center gap-1.5 text-gray-700 whitespace-nowrap">
                            <OwnerAvatar name={owner.fullName} photoUrl={owner.photoUrl ?? null} /> {owner.fullName}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                        {l.sequenceCount > 0 ? `${l.sequenceCount} sequence${l.sequenceCount === 1 ? '' : 's'}` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{formatShortDate(l.updatedAt)}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => open(l)} className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50">
                            View
                          </button>
                          <ActionMenu label={`Actions for ${l.name}`} items={actions.itemsFor(l, { includeView: false })} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!isLoading && rows.length === 0 && (
              <div className="px-4 py-12 text-center">
                {filtered ? (
                  <p className="text-sm text-gray-500">No lists match these filters.</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-brand-darkest">No lists yet</p>
                    <p className="text-sm text-gray-500 mb-3">
                      A list groups contacts for a campaign or a sequence — pick them by hand, or let filters keep it up to date.
                    </p>
                    <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
                      <Plus size={15} /> Create your first list
                    </button>
                  </>
                )}
              </div>
            )}
          </TableCard>
        </div>

        {foldersOpen && (
          // beside the table on a wide screen; on smaller ones it floats over the table's right edge
          // rather than squeezing it into a sideways scroll
          <div className="fixed right-4 top-20 z-40 w-72 max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-xl shadow-xl 2xl:static 2xl:w-auto 2xl:max-h-none 2xl:shadow-none">
            <FoldersPanel selected={folderId} onSelect={(id) => setParam('folder', id)} onClose={() => setFoldersOpen(false)} />
          </div>
        )}
      </div>

      {creating && <ListFormModal defaultFolderId={folderId || null} onClose={() => setCreating(false)} onSaved={open} />}
      {actions.dialog}
    </PageContainer>
  )
}
